'use client'

import React from "react";
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetLeanConsolidatedDataQuery } from '@/redux/slices/preship/preship-api-slice';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { formatSummaryNumber } from '@/lib/formatSummaryNumber';

const sides = ["Side A", "Side B", "Side C", "Side D"];
const statisticsRows = ["Max", "Min", "Average", "StdDev", "Count"];

const SummaryLean = ({ variant, isSum = false }: { variant: string, isSum?: boolean }) => {
  const { projectId } = useSelector((state: RootState) => state.define);
  const { target } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error } = useGetLeanConsolidatedDataQuery({ projectId, variant, isSum }, { refetchOnMountOrArgChange: true });
  console.log(variant, consolidatedData)

  if (isLoading) return (
    <div className="space-y-6 w-full animate-pulse">
      {/* Statistics Table Skeleton */}
      <div className="border rounded-md overflow-hidden">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-6 p-3">
            {Array(6).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="grid grid-cols-6 gap-2 p-2 border-b last:border-0">
              {Array(6).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Additional Stats Table Skeleton */}
      <div className="border rounded-md overflow-hidden mt-4">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-11 p-3">
            {Array(11).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="grid grid-cols-11 gap-2 p-2 border-b last:border-0">
              {Array(11).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (error) return <div>Error loading data</div>;
  if (!consolidatedData) return <div>No data available</div>;

  const getLeanCellColor = (leanValue: string | number) => {
    const numericLean = typeof leanValue === 'string' ? parseFloat(leanValue) : leanValue;
    const numericTarget = Number(target);
    if (!isNaN(numericLean)) {
      if (numericLean >= -numericTarget && numericLean <= numericTarget) {
        return 'bg-states-success text-white';
      } else {
        return 'bg-bgRed text-white';
      }
    }
    return '';
  };

  const renderStatisticsTable = () => (
    <Table className="border-collapse w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">Statistic</TableHead>
          <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">Pallet Type</TableHead>
          {sides.map((side) => (
            <TableHead key={side} className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">
              {side}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {statisticsRows.map((statistic, rowIndex) => {
          const statisticData = consolidatedData.statistics?.[statistic.toLowerCase()];
          if (!statisticData || !Array.isArray(statisticData)) return null;

          return (
            <React.Fragment key={statistic}>
              {statisticData.map((statRow: any, statRowIndex: number) => (
                <TableRow key={`${statistic}-${statRowIndex}`} className={rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  {statRowIndex === 0 && (
                    <TableCell rowSpan={statisticData.length} className="border border-gray-300 text-center p-2 font-semibold">
                      {statistic}
                    </TableCell>
                  )}
                  <TableCell className="border border-gray-300 text-center p-2">{statRow.palletType}</TableCell>
                  {sides.map((side, sideIndex) => {
                    const sideKey = `side${String.fromCharCode(65 + sideIndex)}`;
                    const value = statRow[sideKey];
                    // Apply color coding to Max, Min, and Average since they all represent lean measurements
                    const shouldColorCode = ["Max", "Min", "Average"].includes(statistic);
                    return (
                      <TableCell
                        key={`${statistic}-${side}-${statRowIndex}`}
                        className={cn(
                          "border border-gray-300 text-center p-2",
                          shouldColorCode && getLeanCellColor(value)
                        )}
                      >
                        {statistic === 'Count' ? value : formatSummaryNumber(value)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );

  const renderAdditionalStats = () => {
    if (!consolidatedData.additionalStats || !Array.isArray(consolidatedData.additionalStats)) {
      return <div>No additional statistics available</div>;
    }

    return (
      <Table className="border-collapse w-full mt-4">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">Row Labels</TableHead>
            <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">Data Fill Status</TableHead>
            <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">Overall pass/fail status</TableHead>
            {sides.map((side) => (
              <React.Fragment key={side}>
                <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">{side} Fail Count</TableHead>
                <TableHead className="border border-gray-300 text-center bg-light-blue p-2 font-semibold">{side} Pass Count</TableHead>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.additionalStats.map((row: any, index: number) => (
            <TableRow key={index} className={index % 2 === 0 ? "bg-white text-white" : "bg-gray-50 text-white"}>
              <TableCell className="border border-gray-300 text-center p-2 text-black">{row.rowLabel}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2 text-black">{row.dataFillStatus}</TableCell>
              <TableCell className={cn(
                "border border-gray-300 text-center p-2",
                row.overallStatus === 'Fail' ? "bg-bgRed text-white" :
                  (row.overallStatus === 'Pass' && row.dataFillStatus !== 'Incomplete') ? "bg-states-success text-white" :
                    "bg-[#F79E19] text-white"
              )}>
                {(row.overallStatus === 'Pass' && row.dataFillStatus !== 'Incomplete') ? row.overallStatus :
                  row.overallStatus === 'Fail' ? row.overallStatus : "Data missing"}
              </TableCell>
              {sides.map((side, sideIndex) => (
                <React.Fragment key={`${side}-${index}`}>
                  <TableCell className={cn(
                    "border border-gray-300 text-center p-2",
                    parseInt(row[`side${String.fromCharCode(65 + sideIndex)}FailCount`]) > 0 ? "bg-bgRed" : "bg-states-success"
                  )}>
                    {row[`side${String.fromCharCode(65 + sideIndex)}FailCount`]}
                  </TableCell>
                  <TableCell className={cn(
                    "border border-gray-300 text-center p-2",
                    parseInt(row[`side${String.fromCharCode(65 + sideIndex)}PassCount`]) > 0 ? "bg-states-success" : "bg-bgRed"
                  )}>
                    {row[`side${String.fromCharCode(65 + sideIndex)}PassCount`]}
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-4">
      {renderStatisticsTable()}
      {renderAdditionalStats()}
    </div>
  );
};

export default SummaryLean;
